import { Link, useNavigate } from "react-router-dom";
import { Crown, LogOut, User } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { INTERVALS } from "@/lib/plans";

export const PlanBadge = ({ pro }) => (
  <span
    data-testid="plan-badge"
    className={`font-mono-vox text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${pro ? "border-[#D99B43] text-amber-300" : "border-[#352d40] text-stone-400"}`}
  >
    {pro ? "Pro" : "Free"}
  </span>
);

export const AccountMenu = () => {
  const { user, isPro, logout, openUpgrade, cancelPlan, openPortal } = useAuth();
  const nav = useNavigate();
  if (user === null) return null;
  if (!user)
    return (
      <Link to="/login" data-testid="account-signin-link" className="btn-gothic">
        <User size={14} /> Sign in
      </Link>
    );
  const sub = user.subscription;
  return (
    <div className="flex items-center gap-3">
      {!isPro && (
        <button data-testid="account-upgrade-btn" onClick={() => openUpgrade("Unlimited projects, AI Grimoire, WAV export and loop recording.")} className="btn-gothic !border-[#D99B43]/60 text-amber-300">
          <Crown size={14} /> Upgrade
        </button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="account-menu-trigger" className="flex items-center gap-2 border border-[#352d40] px-2 py-1.5 hover:border-[#D99B43] transition-colors">
            {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <User size={16} className="text-stone-400" />}
            <span className="text-sm text-stone-200 max-w-[120px] truncate">{user.name}</span>
            <PlanBadge pro={isPro} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#141018] border-[#5a1e28] rounded-none w-64">
          <DropdownMenuLabel className="font-normal">
            <p className="text-sm text-stone-200 truncate">{user.email}</p>
            <p className="eyebrow mt-1" data-testid="account-plan-text">
              {!isPro
                ? "Free plan · 2 projects"
                : sub?.provider === "gift"
                  ? `Pro · gift code ${sub.code}`
                  : sub?.provider
                    ? `Pro · ${INTERVALS[sub.interval]?.price || "$9"}/${INTERVALS[sub.interval]?.per || "month"} via ${sub.provider === "stripe" ? "Stripe" : "PayPal"}`
                    : "Pro"}
            </p>
            {isPro && sub?.provider === "gift" && <p className="text-[11px] text-amber-300 mt-1" data-testid="account-gift-text">Gifted Pro until {new Date(sub.gift_ends_at).toLocaleDateString()}</p>}
            {isPro && sub?.trial_ends_at && new Date(sub.trial_ends_at) > new Date() && (
              <p className="text-[11px] text-amber-300 mt-1" data-testid="account-trial-text">Free trial until {new Date(sub.trial_ends_at).toLocaleDateString()}</p>
            )}
            {isPro && sub?.cancel_at_period_end && <p className="text-[11px] text-[#E11D48] mt-1" data-testid="account-cancel-pending">Cancelled · Pro until {new Date(sub.next_billing_time).toLocaleDateString()}</p>}
            {isPro && sub?.next_billing_time && !sub?.cancel_at_period_end && <p className="text-[11px] text-stone-500 mt-1">{sub?.trial_ends_at && new Date(sub.trial_ends_at) > new Date() ? "First charge" : "Renews"} {new Date(sub.next_billing_time).toLocaleDateString()}</p>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[#352d40]" />
          {sub?.provider === "stripe" && (
            <DropdownMenuItem data-testid="account-manage-billing" onSelect={openPortal} className="text-sm">
              <span>
                Manage billing · card, invoices, cancel
                <span className="block text-[11px] text-stone-400 mt-0.5" data-testid="account-card-hint">To change your renewal card, update it on the subscription there.</span>
              </span>
            </DropdownMenuItem>
          )}
          {isPro && sub?.id && !["stripe", "gift"].includes(sub?.provider) && (
            <DropdownMenuItem data-testid="account-cancel-plan" onSelect={cancelPlan} className="text-sm">
              Cancel Pro subscription
            </DropdownMenuItem>
          )}
          {user.role === "admin" && (
            <DropdownMenuItem data-testid="account-admin-codes" onSelect={() => nav("/admin/codes")} className="text-sm">
              Admin · gift & discount codes
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="account-logout" onSelect={logout} className="text-sm">
            <LogOut size={14} className="mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
